import React, { useEffect } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import Nestable, { NestableItem, RenderCollapseIconArgs, RenderItemArgs } from 'react-nestable';
import { addDepths, addItem, addParentIds, editText, getDepth, removeItem } from '../../../../utils/NestableUtils';
import {
    ChapterIndicatorPackageDto,
    ChapterIndicatorThemeDto,
    IndicatorDto,
    UpdateDocumentIndicatorAreaChapterRequest,
} from '../../../../types/indicator';
import { Info, KeyboardArrowDown, KeyboardArrowUp, Save } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChapterDto } from '../../../../types/document';
import { IndicatorNestableItem, IndicatorRenderItemArgs } from '../../../../types/nestable';
import ShvkButton from '../../../../styled/ShvkButton';
import IndicatorNestableComponent from '../../shvk/IndicatorNestableComponent';
import useStore from '../../../../store/storeContext';

const PREFIX = 'IndicatorModelDialog';

const classes = {
    paper: `${PREFIX}-paper`,
};

const StyledDialog = styled(Dialog)(() => ({
    [`& .${classes.paper}`]: {
        height: '90%',
    },
}));

interface Props {
    isOpen: boolean;
    close(): void;
}

interface StoreState {
    items: IndicatorNestableItem[];
    removedPackages: number[];
    removedThemes: number[];
    removedSotkanetIndicators: number[];
    removedOrganizationIndicators: number[];
    removedOtherIndicators: number[];
    getItems: IndicatorNestableItem[];
    indicatorPackages: ChapterIndicatorPackageDto[];
    indicatorModel: UpdateDocumentIndicatorAreaChapterRequest;
}

let nextNewId = -1;

function IndicatorModelDialog(props: Props) {
    const { document, indicator, dialog, localization, loadingIndicator, snackbar, theming } = useStore();

    const localStore = useLocalObservable<StoreState>(() => ({
        items: [],
        removedPackages: [],
        removedThemes: [],
        removedSotkanetIndicators: [],
        removedOrganizationIndicators: [],
        removedOtherIndicators: [],

        get getItems(): IndicatorNestableItem[] {
            return this.items;
        },

        get indicatorPackages(): ChapterIndicatorPackageDto[] {
            return document.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isIndicatorArea)!
                .indicatorPackages;
        },

        get indicatorModel(): UpdateDocumentIndicatorAreaChapterRequest {
            const indicatorChapterId = document.currentDocument.chapters.find(
                (chapter: ChapterDto) => chapter.isIndicatorArea,
            )!.id;

            const packages = this.getItems.map((indicatorNestableItem: IndicatorNestableItem) => {
                const pkg: ChapterIndicatorPackageDto = {
                    id: indicatorNestableItem.id < 0 ? null : indicatorNestableItem.id,
                    chapterId: indicatorChapterId!,
                    name: indicatorNestableItem.text,
                    themes: getAllChildren(indicatorNestableItem),
                    notes: '',
                    description: '',
                    editor: null,
                    lastUpdated: 0,
                    locked: false,
                    version: 0,
                    orderNumber: 0,
                };

                return pkg;
            });

            return {
                documentId: document.currentDocument.id!,
                indicatorModel: packages,
                indicatorPackagesToBeRemoved: this.removedPackages,
                indicatorThemesToBeRemoved: this.removedThemes,
                sotkanetIndicatorsToBeRemoved: this.removedSotkanetIndicators,
                organizationIndicatorsToBeRemoved: this.removedOrganizationIndicators,
                otherIndicatorsToBeRemoved: this.removedOtherIndicators,
            } as UpdateDocumentIndicatorAreaChapterRequest;
        },
    }));

    useEffect(() => {
        props.isOpen && init();
        return () => {
            nextNewId = -1;
        };
    }, [props.isOpen]);

    async function init(): Promise<void> {
        runInAction(() => {
            localStore.removedPackages = [];
            localStore.removedThemes = [];
            localStore.removedSotkanetIndicators = [];
            localStore.removedOrganizationIndicators = [];
            localStore.removedOtherIndicators = [];
        });
        await getData();
        setItems(createNewItems());
    }

    async function getData(): Promise<void> {
        try {
            loadingIndicator.show();
            if (localStore.indicatorPackages) {
                const packagesWithoutThemes: Array<number> = [];
                localStore.indicatorPackages.forEach((indicatorPackage: ChapterIndicatorPackageDto) => {
                    if (indicatorPackage.themes.length === 0) {
                        packagesWithoutThemes.push(indicatorPackage.id!);
                    }
                });
                if (packagesWithoutThemes.length > 0) {
                    await Promise.all(
                        packagesWithoutThemes.map((indicatorPackageId: number) =>
                            indicator.fetchPackageThemes(indicatorPackageId),
                        ),
                    );
                }
            }
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            loadingIndicator.hide();
        }
    }

    async function handleReset(): Promise<void> {
        const { translate } = localization;

        const confirmTitle = translate('GENERAL_CONFIRMATION_TITLE');
        const confirmText = translate('CONFIRM_INDICATOR_MODEL_RESET');
        const confirmation = await dialog.getConfirmation(confirmTitle, confirmText, true);
        if (!confirmation) return;

        try {
            loadingIndicator.show();
            await document.resetIndicatorModel();
            await init();
            snackbar.showSuccess();
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            loadingIndicator.hide();
        }
    }

    async function save(): Promise<void> {
        try {
            loadingIndicator.show();
            await document.updateDocumentIndicatorAreaChapter(localStore.indicatorModel);
            await init();
            snackbar.showSuccess();
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            loadingIndicator.hide();
        }
    }

    function setItems(newItems: IndicatorNestableItem[]): void {
        runInAction(() => (localStore.items = newItems));
    }

    function createNewItems(): IndicatorNestableItem[] {
        if (localStore.indicatorPackages) {
            return localStore.indicatorPackages.map((indicatorPackage: ChapterIndicatorPackageDto) => {
                return createLevel1Item(indicatorPackage);
            });
        } else return [] as IndicatorNestableItem[];
    }

    function getAllChildren(indicatorNestableItem: IndicatorNestableItem): ChapterIndicatorThemeDto[] {
        const children: ChapterIndicatorThemeDto[] = [];
        indicatorNestableItem.children.forEach((themeLevel0: IndicatorNestableItem) => {
            const theme: ChapterIndicatorThemeDto = {
                id: themeLevel0.id < 0 ? null : themeLevel0.id,
                icon: null,
                level: 0,
                options: 0,
                name: themeLevel0.text,
                orderNumber: 0,
                info: null,
                pkgId: themeLevel0.parentId || 0,
                sourceThemeId: null,
                indicators: [],
            };
            children.push(theme);

            themeLevel0.children.forEach((themeLevel1: IndicatorNestableItem) => {
                const childTheme: ChapterIndicatorThemeDto = {
                    id: themeLevel1.id < 0 ? null : themeLevel1.id,
                    icon: null,
                    level: 1,
                    options: 0,
                    name: themeLevel1.text,
                    orderNumber: 0,
                    info: null,
                    pkgId: themeLevel0.parentId || 0,
                    sourceThemeId: null,
                    indicators: addIndicators(themeLevel1),
                };

                children.push(childTheme);
            });
        });
        return children;
    }

    function addIndicators(theme: IndicatorNestableItem): IndicatorDto[] {
        const indicators = theme.children;
        return indicators.map((indicator: IndicatorNestableItem) => {
            const ind: IndicatorDto = {
                id: indicator.id < 0 ? null : indicator.id,
                sourceCode: indicator.indicatorSource! as 'CHP_SOTKANET' | 'CHP_CUSTOM' | 'CHP_OTHER',
                sourceName: '',
                name: indicator.text,
                orderNumber:
                    theme.children.findIndex((indItem: IndicatorNestableItem) => indItem.id === indicator.id) + 1,
                textOnly: false,
                alarmMax: -1,
                alarmMin: -1,
                targetLimit: -1,
                direction: null,
                code: null,
                indicatorSourceId: null,
                infoUrl: null,
                themeId: theme.id,
                type: null,
                description: null,
                organization: null,
                data: [],
                metadata: null,
                sotkanetId: null,
                origoOrganization: null,
                enabled: null,
                removable: null,
                printable: null,
                shortTermChangeData: [],
                indicatorTopTitle: null,
            };
            return ind;
        });
    }

    function createLevel1Item(indicatorPackage: ChapterIndicatorPackageDto): IndicatorNestableItem {
        return {
            id: indicatorPackage.id!,
            children: addChildrenToLevel1Item(indicatorPackage),
            parentId: null,
            depth: 1,
            text: indicatorPackage.name,
            isIndicator: false,
            isRemovable: true,
        };
    }

    function addChildrenToLevel1Item(indicatorPackage: ChapterIndicatorPackageDto): IndicatorNestableItem[] {
        return indicatorPackage.themes
            .filter((indicatorPackageTheme: ChapterIndicatorThemeDto) => indicatorPackageTheme.level === 0)
            .map((indicatorPackageTheme: ChapterIndicatorThemeDto) => {
                return createLevel2Item(indicatorPackage, indicatorPackageTheme);
            });
    }

    function createLevel2Item(
        indicatorPackage: ChapterIndicatorPackageDto,
        indicatorPackageTheme: ChapterIndicatorThemeDto,
    ): IndicatorNestableItem {
        return {
            id: indicatorPackageTheme.id!,
            children: addChildrenToLevel2Item(indicatorPackage, indicatorPackageTheme.id!),
            parentId: indicatorPackageTheme.pkgId,
            depth: 2,
            text: indicatorPackageTheme.name || '',
            isIndicator: false,
            isRemovable: true,
        };
    }

    function addChildrenToLevel2Item(
        indicatorPackage: ChapterIndicatorPackageDto,
        parentId: number,
    ): IndicatorNestableItem[] {
        const children: IndicatorNestableItem[] = [];
        let filter = true;

        indicatorPackage.themes.forEach((indicatorPackageTheme: ChapterIndicatorThemeDto) => {
            if (indicatorPackageTheme.level === 1 && !filter)
                children.push(createLevel3Item(indicatorPackageTheme, parentId));
            else if (indicatorPackageTheme.level === 0 && indicatorPackageTheme.id === parentId) filter = false;
            else if (indicatorPackageTheme.level === 0 && indicatorPackageTheme.id !== parentId) filter = true;
        });
        return children;
    }

    function createLevel3Item(
        indicatorPackageTheme: ChapterIndicatorThemeDto,
        parentId: number,
    ): IndicatorNestableItem {
        return {
            id: indicatorPackageTheme.id!,
            children: addChildrenToLevel3Item(indicatorPackageTheme, indicatorPackageTheme.id!),
            parentId: parentId,
            depth: 3,
            text: indicatorPackageTheme.name || '',
            isIndicator: false,
            isRemovable: true,
        };
    }

    function addChildrenToLevel3Item(
        indicatorPackageTheme: ChapterIndicatorThemeDto,
        parentId: number,
    ): IndicatorNestableItem[] {
        const allIndicators = indicator.sortByOrderNumber([...indicatorPackageTheme.indicators]);
        return allIndicators.map((indicator: IndicatorDto) => {
            return createIndicatorItem(indicator, parentId);
        });
    }

    function createIndicatorItem(indicator: IndicatorDto, parentId: number): IndicatorNestableItem {
        return {
            id: indicator.id!,
            children: [],
            parentId: parentId,
            depth: 4,
            text: indicator.name,
            isIndicator: true,
            indicatorSource: indicator.sourceCode,
            indicatorType: indicator.type || '',
            orderNumber: indicator.orderNumber!,
            isRemovable: indicator.removable!,
        };
    }

    function removeIndicatorNestableItem(item: IndicatorNestableItem): void {
        const newItems = localStore.getItems.filter(removeItem(item));
        setItems(newItems as IndicatorNestableItem[]);

        if (item.id > 0) {
            if (!item.isIndicator && item.depth === 1) {
                // Removed item is package.
                runInAction(() => localStore.removedPackages.push(item.id));
            } else if (!item.isIndicator && (item.depth === 2 || item.depth === 3)) {
                // Removed item is theme.
                runInAction(() => localStore.removedThemes.push(item.id));
            } else {
                // Removed item is indicator.
                const indicator = document.findIndicatorById(item.id);
                if (indicator) {
                    if (indicator.sourceCode === 'CHP_SOTKANET') {
                        runInAction(() => localStore.removedSotkanetIndicators.push(item.id));
                    }
                    if (indicator.sourceCode === 'CHP_CUSTOM') {
                        runInAction(() => localStore.removedOrganizationIndicators.push(item.id));
                    }
                    if (indicator.sourceCode === 'CHP_OTHER') {
                        runInAction(() => localStore.removedOtherIndicators.push(item.id));
                    }
                }
            }
        }
    }

    function addIndicatorNestableItem(parentId: number | null = null, isIndicator = false, isRemovable = true): void {
        const { translate } = localization;
        const depth = parentId === null ? 1 : getDepth(parentId, localStore.getItems) + 1;
        const newItem: IndicatorNestableItem = {
            id: nextNewId--,
            text: depth === 1 ? translate('NEW_MAIN_DIRECTORY') : translate('NEW_SUB_DIRECTORY'),
            children: [],
            parentId,
            depth,
            isIndicator,
            isRemovable,
        };
        const currentItems: IndicatorNestableItem[] = [...localStore.getItems];
        if (parentId !== null) {
            // A new subdirectory
            const newItems = currentItems.map(addItem(newItem, parentId));
            setItems(newItems as IndicatorNestableItem[]);
        } else {
            // A new main directory
            const newItems = [...currentItems, newItem];
            handleListChange(newItems);
        }
    }

    function confirmChange(dragItem: NestableItem, destinationParent: NestableItem): boolean {
        const indicatorDragItem = dragItem as IndicatorNestableItem;
        const indicatorDestinationParent = destinationParent as IndicatorNestableItem;
        //Package item can't change depth
        if (indicatorDragItem.depth === 1 && indicatorDestinationParent) return false;
        //Theme must be child to level1 or level 2 item
        else if (
            (indicatorDragItem.depth === 2 || indicatorDragItem.depth === 3) &&
            !(indicatorDestinationParent?.depth === 1 || indicatorDestinationParent?.depth === 2)
        )
            return false;
        //Indicator must be child to level 3 Theme
        else if (indicatorDragItem.isIndicator && indicatorDestinationParent?.depth !== 3) return false;
        //Item with children can't change depth
        else if (
            indicatorDragItem.children.length > 0 &&
            indicatorDestinationParent?.depth !== (indicatorDragItem.depth - 1 || undefined)
        )
            return false;
        else return true;
    }

    function renderItem(args: RenderItemArgs): React.ReactNode {
        return (
            <IndicatorNestableComponent
                args={args as IndicatorRenderItemArgs}
                handleTextChange={handleTextChange}
                addItem={addIndicatorNestableItem}
                removeItem={removeIndicatorNestableItem}
                handleAddIndicator={handleAddIndicator}
                save={save}
            />
        );
    }

    function renderCollapseIcon(args: RenderCollapseIconArgs): JSX.Element {
        return (
            <IconButton size="small" color="primary">
                {args.isCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            </IconButton>
        );
    }

    const handleTextChange = (value: string, id: number): void => {
        const newItems = localStore.getItems.map(editText(id, value));
        setItems(newItems as IndicatorNestableItem[]);
    };

    const handleListChange = (items: NestableItem[]): void => {
        const indicatorItems = items as IndicatorNestableItem[];
        let newItems = indicatorItems.map(addParentIds());
        newItems = newItems.map(addDepths());
        setItems(newItems as IndicatorNestableItem[]);
    };

    const handleAddIndicator = (indicator: IndicatorDto, parentId: number): void => {
        const newIndicator = createIndicatorItem(indicator, parentId);
        const indicators = [...localStore.getItems];
        indicators.forEach((item) => {
            item.children.forEach((child) => {
                child.children.forEach((theme) => {
                    if (theme.id === parentId) {
                        theme.children.push(newIndicator);
                    }
                });
            });
        });
        handleListChange(indicators);
    };

    const { translate } = localization;

    return (
        <StyledDialog
            id="chapter-model-dialog"
            open={props.isOpen}
            onClose={props.close}
            maxWidth="md"
            fullWidth
            classes={{ paper: classes.paper }}
        >
            <DialogTitle id="chapter-model-dialog-title">
                {translate('DOCUMENT_INDICATOR_MODEL_EDIT_TITLE')}
                <Box pt={1} display="flex">
                    <Info style={{ marginRight: theming.spacing(1) }} color="primary" />
                    <Typography variant="body2">{translate('DOCUMENT_INDICATOR_MODEL_EDIT_TIP')}</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Nestable
                    items={localStore.getItems}
                    renderItem={renderItem}
                    onChange={handleListChange}
                    confirmChange={confirmChange}
                    maxDepth={4}
                    renderCollapseIcon={renderCollapseIcon}
                    collapsed={true}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={handleReset}>
                    {translate('INDICATOR_MODEL_SET_AS_DEFAULT')}
                </Button>
                <ShvkButton
                    onClick={(): void => {
                        addIndicatorNestableItem(null);
                    }}
                >
                    {translate('ADD_INDICATOR_PACKAGE')}
                </ShvkButton>
                <Box flexGrow={1} />
                <Button variant="contained" onClick={props.close}>
                    {translate('CLOSE')}
                </Button>
                <ShvkButton startIcon={<Save />} onClick={save}>
                    {translate('SAVE')}
                </ShvkButton>
            </DialogActions>
        </StyledDialog>
    );
}

export default observer(IndicatorModelDialog);
